import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { Box, CircularProgress, IconButton } from "@material-ui/core";

import { CustomTab, CustomTabs } from "../../components/Tabs";
import TabPanel from "../../components/TabPanel";
import useStyles from "../business/BusinessTabPanel.style";
import Business, { BusinessScopes } from "../../../../models/Business";
import BusinessService from "../../../../services/BusinessService";
import AvatarHeader from "../../components/AvatarHeader";
import CloseIcon from "@material-ui/core/SvgIcon/SvgIcon";
import { NotificationProvider } from "../../../../context/NotificationContext";
import { NotificationWrapper } from "../../components/GenericWrapper";
import { AppScrollbar } from "../../../../components/scrollbar/AppScrollbar";
import BusinessOverview from "./components/BusinessOverview";
import WalletOverview from "./components/WalletOverview";
import ActivityOverview from "./components/ActivityOverview";
import BusinessBranchOverview from "./components/BusinessBranchOverview";
import BusinessTeamOverview from "./components/BusinessTeamOverview";

interface BusinessTabPanelProps {}

enum LoadingStatus {
  loading,
  failure,
  succeed,
  initial,
  adding,
}

interface BusinessTabPanelProps {
  business: Business;
  refreshCallback: (business: Business) => void;
  onClose: () => void;
}

export const BusinessTabPanel = (props: BusinessTabPanelProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  const { onClose, business, refreshCallback } = props;
  // State
  const [tab, setTab] = useState(0);
  const [remoteBusiness, setRemoteBusiness] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(
    (): LoadingStatus => LoadingStatus.initial
  );

  const fetchBusiness = () => {
    setLoadingStatus(LoadingStatus.loading);

    BusinessService.getById(
      business.id,
      `${BusinessScopes.docs}|${BusinessScopes.accounts}|${BusinessScopes.owner}|${BusinessScopes.bank}`
    ).then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let business = Business.fromJson(
          JSON.parse(JSON.stringify(response.data.data))
        );

        setRemoteBusiness(business);
        setLoadingStatus(LoadingStatus.succeed);
      } else {
        setLoadingStatus(LoadingStatus.failure);
      }
    });
  };

  const refreshBusinessCallback = (business: Business) => {
    setRemoteBusiness(business);
    // Refresh business paginator
    refreshCallback(business);
  };

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  const renderBusinessTabs = () => {
    if (business.isApproved()) {
      return (
        <Box pl={4} pr={4}>
          <CustomTabs
            value={tab}
            onChange={handleChange}
            aria-label="Business tabs"
          >
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.overview" })}
            />
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.wallet" })}
            />
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.activities" })}
            />
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.branches" })}
            />
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.teams" })}
            />
          </CustomTabs>
        </Box>
      );
    }

    return (
      <Box pl={4} pr={4}>
        <CustomTabs
          value={tab}
          onChange={handleChange}
          aria-label="Business tabs"
        >
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.overview" })}
          />
        </CustomTabs>
      </Box>
    );
  };

  const renderBusinessPanel = () => {
    if (business.isApproved()) {
      return (
        <>
          <TabPanel id="team-member-tabs-tabpanel" value={tab} index={0}>
            <BusinessOverview
              refreshCallback={refreshBusinessCallback}
              business={remoteBusiness}
            />
          </TabPanel>
          <TabPanel id="wallet-tabs-tabpanel" value={tab} index={1}>
            <WalletOverview business={remoteBusiness} />
          </TabPanel>
          <TabPanel id="branches-tabs-tabpanel" value={tab} index={2}>
            <ActivityOverview business={remoteBusiness} />
          </TabPanel>
          <TabPanel id="branches-tabs-tabpanel" value={tab} index={3}>
            <BusinessBranchOverview business={remoteBusiness} />
          </TabPanel>
          <TabPanel id="team-tabs-tabpanel" value={tab} index={4}>
            <BusinessTeamOverview business={remoteBusiness} />
          </TabPanel>
        </>
      );
    }

    return (
      <>
        <TabPanel id="team-member-tabs-tabpanel" value={tab} index={0}>
          <BusinessOverview
            refreshCallback={refreshBusinessCallback}
            business={remoteBusiness}
          />
        </TabPanel>
      </>
    );
  };

  const renderHeaderWrapper = () => {
    let defaultBusiness = remoteBusiness !== null ? remoteBusiness : business;

    return (
      <Box component="div" className={classes.headerWrapper}>
        <Box component="div" className={classes.header}>
          <Box display="flex" alignItems="center">
            <Box style={{ flexGrow: 1 }}>
              <AvatarHeader
                avatar={defaultBusiness.avatar()}
                validationStatus={defaultBusiness.status()}
                accountStatus={defaultBusiness.status()}
                subTitle={defaultBusiness.address()}
                title={defaultBusiness.fullName()}
              />
            </Box>
            {loadingStatus === LoadingStatus.loading ? (
              <Box component="div" mr={2} style={{ marginTop: "10px" }}>
                <CircularProgress
                  color="secondary"
                  disableShrink={false}
                  size={20}
                  thickness={4}
                />
              </Box>
            ) : (
              <>
                <IconButton
                  href=""
                  aria-label="close"
                  onClick={(_) => onClose()}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    fetchBusiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NotificationProvider>
      <Box component="div" className={classes.root}>
        <NotificationWrapper />
        {renderHeaderWrapper()}
        {loadingStatus == LoadingStatus.succeed && remoteBusiness !== null ? (
          <>
            <Box component="div" className={classes.tabsWrapper}>
              <Box component="div">{renderBusinessTabs()}</Box>
            </Box>
            <AppScrollbar>
              <Box component="div" className={classes.tabsPanelWrapper}>
                <Box component="div" style={{ paddingBottom: "4rem" }}>
                  {renderBusinessPanel()}
                </Box>
              </Box>
            </AppScrollbar>
          </>
        ) : (
          <></>
        )}
      </Box>
    </NotificationProvider>
  );
};
